import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, XCircle, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtTs = (s) => { try { return s ? new Date(s).toUTCString() : "—"; } catch { return s || "—"; } };

const Row = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-[#2A2A22]">
    <div className="sm:w-52 shrink-0 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
    <div className={`text-[13px] text-[#ECECE4] ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value}</div>
  </div>
);

export default function MedallionVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/verify/medallion/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "This medallion guarantee could not be verified.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="medallion-verify-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="mv-home-link">← Return to Home</Link>

        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            <ShieldCheck size={22} className="text-[#B8860B]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">S.T.A.M.P. Medallion Signature Guarantee</div>
              <div className="text-xl font-serif">Verification Certificate</div>
            </div>
          </div>

          {error ? (
            <div className="px-7 py-12 text-center" data-testid="mv-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !data ? (
            <div className="px-7 py-16 text-center" data-testid="mv-loading"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="mv-result">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 text-[11px] uppercase tracking-[0.14em] font-bold bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full" data-testid="mv-verified-badge">
                <BadgeCheck size={14} /> Guarantee Verified
              </div>
              <Row label="Guarantee No." value={<span className="font-semibold text-[#E4C97A]">{data.medallion_no}</span>} />
              <Row label="Program" value={data.program} />
              <Row label="Guarantor" value={data.guarantor} />
              <Row label="Instrument" value={`Private Negotiable Instrument No. ${data.draft_no || "—"}`} />
              <Row label="Face Amount" value={fmtUSD(data.amount)} />
              <Row label="Drawer / Maker" value={data.drawer_name || "—"} />
              <Row label="Payee / Counterparty" value={data.pay_to || "—"} />
              <Row label="Maturity" value={data.maturity_date || "—"} />
              <Row label="Vault ID" value={data.vault_id} mono />
              <Row label="Status" value={<span className="uppercase">{data.status}</span>} />
              <Row label="Vaulted (UTC)" value={fmtTs(data.vaulted_at)} />
              <Row label="Instrument SHA-256" value={data.sha256 || "—"} mono />
              <Row label="Medallion SHA-256" value={data.medallion_hash || "—"} mono />
              {data.reserve_backing?.backed && (
                <Row label="Reserve Backing" value={
                  <span className="text-[#3DBB8E]" data-testid="mv-reserve-backing">✓ Reserve-Backed · {data.reserve_backing.reserve_no} · ratio {(data.reserve_backing.ratio * 100).toFixed(0)}%</span>
                } />
              )}
              <p className="mt-5 text-[11px] text-[#5C5C5C] leading-relaxed">
                This read-only certificate confirms the S.T.A.M.P. medallion guarantee registered for the instrument above.
                It does not redeem, transfer, or negotiate the instrument. Retain this reference for your records.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
